/**
 * Reset Stores Module
 *
 * Provides a centralized way to reset all Pinia stores when a user logs out
 * or switches tenants. This prevents data leakage between users/tenants.
 *
 * Uses getActivePinia() to access already-instantiated stores without
 * calling useXxxStore() which requires inject() (Vue setup context).
 *
 * SECURITY: This is critical for multi-tenant isolation.
 */

import { getActivePinia } from 'pinia'
import type { Pinia, Store } from 'pinia'

// LocalStorage keys that contain user/tenant-specific data
const USER_SPECIFIC_STORAGE_KEYS = [
  'minimized-documents',
  'help-center-state',
  'recent-searches',
  'kanban-collapsed-columns',
  'document-viewer-state',
]

// Store IDs that should NOT be reset (auth manages its own state)
const EXCLUDED_STORE_IDS = ['auth']

/**
 * Resets all data stores to their initial state.
 * Uses Pinia's internal store registry to avoid inject() issues.
 *
 * Call this when:
 * - User logs out
 * - User switches tenants (impersonation)
 * - Session expires
 */
export function resetAllDataStores(): void {
  const pinia = getActivePinia() as Pinia & {
    _s: Map<string, Store>
  }

  if (!pinia) {
    // eslint-disable-next-line no-console
    console.warn('resetAllDataStores: No active Pinia instance found')
    return
  }

  // Iterate over all registered stores and reset them
  pinia._s.forEach((store, id) => {
    if (EXCLUDED_STORE_IDS.includes(id)) return

    try {
      if (typeof store.$reset === 'function') {
        store.$reset()
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to reset store "${id}":`, error)
    }
  })

  // Clear user-specific localStorage items
  clearUserSpecificStorage()
}

/**
 * Clears localStorage items that contain user/tenant-specific data
 */
function clearUserSpecificStorage(): void {
  USER_SPECIFIC_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to remove localStorage key "${key}":`, error)
    }
  })
}
